<script setup lang="ts">
import { onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';  
import Page6Policy from '@/assets/svg/home/page6/page6-policy.svg';
import Page6System from '@/assets/svg/home/page6/page6-system.svg';
import Page6Update from '@/assets/svg/home/page6/page6-update.svg';
import Page6Standard from '@/assets/svg/home/page6/page6-standard.svg';
import Page6Stage from '@/assets/svg/home/page6/page6-stage.svg';
import Page6Practice from '@/assets/svg/home/page6/page6-practice.svg';

const { t } = useI18n();
function useScrollAnimate() {
  const io = new IntersectionObserver(
    entries => {
      entries.forEach(e => {
        const el = e.target as HTMLElement
        if (e.isIntersecting) {
          el.classList.add('animate-in')
          el.classList.remove('animate-out')
        } else {
          el.classList.add('animate-out')
          el.classList.remove('animate-in')
        }
      })
    },
    {
      threshold: [0, 0.15, 0.5, 1] // 多个阈值，避免闪烁
    }
  );

  const vScrollAnimate = {
    mounted(el: HTMLElement) {
      el.classList.add('animate-out'); // 初始就加 animate-out 防止第一次闪
      io.observe(el);
    },
    unmounted(el: HTMLElement) {
      io.unobserve(el);
    }
  };

  onBeforeUnmount(() => io.disconnect());
  return vScrollAnimate;
}
const vScrollAnimate = useScrollAnimate();

</script>

<template>
  <div class="bg-[#FAFAFA] py-[5rem]">
    <div class="max-width mx-auto">
      <!-- 顶部标题 -->
      <div class="text-center mb-[4rem]" v-scroll-animate>
        <h2 class="text-[3rem] font-[600] text-[#000]">
          {{ t('page6.heading.main') }}
          <span class="text-main-primary">{{ t('page6.heading.highlight') }}</span>
        </h2>
        <p class="text-[1.25rem] text-[#000]">
          {{ t('page6.heading.desc') }}
        </p>
      </div>
      <!-- 六大特点 -->
      <div class="relative mx-[3rem] flex justify-between">
        <img src="https://iiii-clone.oss-cn-shenzhen.aliyuncs.com/web/nbweb/assets/img/home/page6/page6-center.png"
             class="absolute left-[50%] top-[48%] translate-x-[-50%] translate-y-[-50%] w-[17.8rem] h-[17.8rem]" />

        <!-- 左列 -->
        <div class="flex flex-col" v-scroll-animate>
          <!-- 政策指导 -->
          <div class="h-[12rem]">
            <div class="flex relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[34rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.policy.title') }}</h3>
              <Page6Policy class="w-[3.48rem] absolute right-[-1.5rem] bottom-[-3.2rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <p class="text-[1rem] w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.policy.desc') }}
            </p>
          </div>

          <!-- 系统性 -->
          <div class="h-[12rem]">
            <div class="flex relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[27.4rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.system.title') }}</h3>
              <Page6System class="w-[3.48rem] absolute right-[-1.5rem] bottom-[-2.4rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <p class="text-[1rem] w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.system.desc') }}
            </p>
          </div>

          <!-- 实时更新 -->
          <div>
            <div class="flex relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[34rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.update.title') }}</h3>
              <Page6Update class="w-[3.48rem] absolute right-[-1.5rem] bottom-[-1.3rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <p class="text-[1rem] w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.update.desc') }}
            </p>
          </div>
        </div>

        <!-- 右列 -->
        <div class="flex flex-col" v-scroll-animate>
          <!-- 权威标准 -->
          <div class="flex flex-col items-end h-[12rem]">
            <div class="flex justify-end relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[34rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.standard.title') }}</h3>
              <Page6Standard class="w-[3.48rem] absolute left-[-1.5rem] bottom-[-3.2rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <div class="text-[1rem] text-right w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.standard.desc') }}
            </div>
          </div>

          <!-- 分阶段 -->
          <div class="flex flex-col items-end h-[12rem]">
            <div class="flex justify-end relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[26.5rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.stage.title') }}</h3>
              <Page6Stage class="w-[3.48rem] absolute left-[-1.5rem] bottom-[-2.5rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <p class="text-[1rem] text-right w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.stage.desc') }}
            </p>
          </div>

          <!-- 实践导向 -->
          <div class="flex flex-col items-end">
            <div class="flex justify-end relative pb-[0.5rem] border-b-[0.5px] border-main-primary/30 w-[34rem] items-center mb-[1rem]">
              <h3 class="text-[1.375rem] text-main-primary">{{ t('page6.cards.practice.title') }}</h3>
              <Page6Practice class="w-[3.48rem] absolute left-[-1.5rem] bottom-[-1.3rem] h-[4.8rem] mr-[1rem]" />
            </div>
            <p class="text-[1rem] text-right w-[20.7rem] font-[500] text-[#3d3d3d]">
              {{ t('page6.cards.practice.desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 初始和离开状态 */
.animate-out {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

/* 进入视口的状态 */
.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
</style>